/**
 * Iter 151 — Brand Content-Planning Cockpit
 *
 * Route: /app/brands/:brand_id/cockpit
 *
 * A single-page dashboard for whoever runs a brand's social. Combines
 * marketable-days, featured trends, playbook suggestions, calendar heatmap,
 * campaigns (scope=brand), outreach (scope=brand_partnership), and the
 * brand's marketing channels (manual-entry). Uses one aggregate endpoint
 * to keep the page fast.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useWorkspacePerms } from "@/lib/useWorkspacePerms";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { LayoutDashboard, CalendarDays, TrendingUp, Layers, Send, Plus, Trash2, Edit3, ExternalLink, Instagram, Facebook, Linkedin, Twitter, Youtube, Mail, Globe, Rocket, PhoneCall, Sparkles, MessageCircle, X, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { fmtRelative } from "@/lib/format";

const PLATFORM_ICONS = {
  instagram: Instagram, facebook: Facebook, linkedin: Linkedin,
  twitter: Twitter, youtube: Youtube, email: Mail, whatsapp: MessageCircle, other: Globe,
};

const CAMPAIGN_STATUS_TONE = {
  draft: "chip", live: "chip-approved", paused: "chip-warning", done: "chip",
};
const OUTREACH_STATUS_TONE = {
  to_reach: "chip", contacted: "chip-warning", responded: "chip-approved",
  declined: "chip-rejected", won: "chip-approved",
};

export default function BrandCockpit() {
  const { brand_id } = useParams();
  const nav = useNavigate();
  const { activeWorkspaceId } = useAuth();
  const { can } = useWorkspacePerms(activeWorkspaceId);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [campaignEdit, setCampaignEdit] = useState(null);
  const [outreachEdit, setOutreachEdit] = useState(null);
  const [channelsEdit, setChannelsEdit] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null); // {kind, id, name}
  const canManage = can("marketing.manage") || can("brand.manage");

  const load = useCallback(async () => {
    setErr(null);
    try {
      const { data: d } = await api.get(`/brands/${brand_id}/cockpit`);
      setData(d);
    } catch (e) {
      setErr(apiErrorMessage(e, "Couldn't load cockpit"));
    }
  }, [brand_id]);
  useEffect(() => { load(); }, [load]);

  if (err) return <div className="p-6 text-sm text-destructive">{err}</div>;
  if (!data) return <div className="p-6 text-sm text-muted-foreground">Loading brand cockpit…</div>;

  const brand = data.brand;

  const doDelete = async () => {
    if (!confirmDel) return;
    try {
      if (confirmDel.kind === "campaign") await api.delete(`/campaigns/${confirmDel.id}`);
      if (confirmDel.kind === "outreach") await api.delete(`/outreach-targets/${confirmDel.id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Delete failed"));
    } finally { setConfirmDel(null); }
  };

  return (
    <>
      <PageHeader
        breadcrumb="Brands"
        title={brand.name}
        description={
          <span>
            {brand.industry ? `${brand.industry}${brand.sub_industry ? ` · ${brand.sub_industry}` : ""} · ` : ""}
            Content-planning cockpit — marketable days, trends, playbooks, campaigns and outreach for this brand.
          </span>
        }
        testid="brand-cockpit-header"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-none" onClick={() => nav(`/app/brands`)} data-testid="cockpit-back">
              ← Brands
            </Button>
          </div>
        }
      />

      <div className="px-4 sm:px-6 md:px-10 py-4 space-y-6" data-testid="brand-cockpit">
        {/* Marketing Channels */}
        <ChannelsSection
          brand={brand}
          canEdit={canManage}
          onEdit={() => setChannelsEdit(true)}
        />

        {/* Two-column: Upcoming days + Featured trends */}
        <div className="grid gap-4 lg:grid-cols-2">
          <UpcomingDaysSection days={data.upcoming_days} />
          <FeaturedTrendsSection trends={data.featured_trends} onPushed={load} />
        </div>

        {/* Playbooks + Heatmap */}
        <div className="grid gap-4 lg:grid-cols-2">
          <SuggestedPlaybooksSection playbooks={data.suggested_playbooks} brandId={brand.id} onImported={load} />
          <HeatmapSection heatmap={data.heatmap} brandId={brand.id} onChanged={load} />
        </div>

        {/* Brand Campaigns */}
        <CampaignsSection
          campaigns={data.campaigns}
          canEdit={canManage}
          onNew={() => setCampaignEdit({ scope: "brand", brand_id: brand.id, status: "draft" })}
          onEdit={(c) => setCampaignEdit(c)}
          onDelete={(c) => setConfirmDel({ kind: "campaign", id: c.id, name: c.name })}
        />

        {/* Outreach */}
        <OutreachSection
          items={data.outreach}
          canEdit={canManage}
          onNew={() => setOutreachEdit({ scope: "brand_partnership", brand_id: brand.id, status: "to_reach", channel: "email" })}
          onEdit={(o) => setOutreachEdit(o)}
          onDelete={(o) => setConfirmDel({ kind: "outreach", id: o.id, name: o.name })}
        />
      </div>

      {campaignEdit && (
        <CampaignEditor
          wsId={activeWorkspaceId}
          initial={campaignEdit}
          onClose={() => setCampaignEdit(null)}
          onSaved={() => { setCampaignEdit(null); load(); }}
        />
      )}
      {outreachEdit && (
        <OutreachEditor
          wsId={activeWorkspaceId}
          initial={outreachEdit}
          onClose={() => setOutreachEdit(null)}
          onSaved={() => { setOutreachEdit(null); load(); }}
        />
      )}
      {channelsEdit && (
        <ChannelsEditor
          brand={brand}
          onClose={() => setChannelsEdit(false)}
          onSaved={() => { setChannelsEdit(false); load(); }}
        />
      )}
      <AlertDialog open={!!confirmDel} onOpenChange={(o) => { if (!o) setConfirmDel(null); }}>
        <AlertDialogContent data-testid="cockpit-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {confirmDel?.kind}?</AlertDialogTitle>
            <AlertDialogDescription>&ldquo;{confirmDel?.name}&rdquo; will be removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={doDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ─────────── Sections ───────────

function ChannelsSection({ brand, canEdit, onEdit }) {
  const channels = brand.marketing_channels || [];
  return (
    <section className="border border-border" data-testid="cockpit-channels">
      <SectionHead
        icon={Globe} title="Marketing channels"
        count={channels.length}
        action={canEdit && <Button size="sm" variant="outline" className="rounded-none h-7" onClick={onEdit} data-testid="cockpit-channels-edit"><Edit3 className="w-3 h-3 mr-1" /> Edit</Button>}
      />
      {channels.length === 0 ? (
        <div className="p-3 text-xs italic text-muted-foreground">
          No channels configured yet — {canEdit ? "click Edit to add IG/FB/LinkedIn handles." : "ask an admin to set them up."}
        </div>
      ) : (
        <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3 p-3">
          {channels.map((c, i) => {
            const Icon = PLATFORM_ICONS[c.platform] || Globe;
            return (
              <div key={i} className="border border-border p-2 flex items-center gap-2" data-testid={`cockpit-channel-${c.platform}`}>
                <Icon className="w-4 h-4 text-primary" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium truncate">{c.handle || c.platform}</div>
                  {c.url && (
                    <a href={c.url} target="_blank" rel="noreferrer" className="text-[10px] text-muted-foreground underline truncate block">
                      {c.url.replace(/^https?:\/\//, "")}
                    </a>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <div className="text-xs font-mono">{typeof c.followers_manual === "number" ? c.followers_manual.toLocaleString() : "—"}</div>
                  <div className="text-[9px] text-muted-foreground">followers</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function UpcomingDaysSection({ days }) {
  return (
    <section className="border border-border" data-testid="cockpit-days">
      <SectionHead icon={CalendarDays} title="Upcoming marketable days" count={days.length} sub="matched to this brand's industry" />
      {days.length === 0 ? (
        <div className="p-3 text-xs italic text-muted-foreground">Nothing on the horizon — add custom days from the Marketable Days master calendar.</div>
      ) : (
        <ul className="divide-y divide-border max-h-80 overflow-y-auto">
          {days.slice(0, 15).map((d) => (
            <li key={d.id} className="p-2 flex items-center gap-2 text-xs">
              <span className="w-16 font-mono text-primary shrink-0">{d.date?.slice(5)}</span>
              <span className="text-lg leading-none">{d.emoji || "📅"}</span>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{d.name}</div>
                {d.description && <div className="text-[10px] text-muted-foreground truncate">{d.description}</div>}
              </div>
              {d.category && <span className="chip text-[10px]">{d.category}</span>}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function FeaturedTrendsSection({ trends, onPushed }) {
  return (
    <section className="border border-border" data-testid="cockpit-trends">
      <SectionHead icon={Sparkles} title="Featured trends for this brand" count={trends.length}
                   sub="scored by our daily 04:00 UTC curation" />
      {trends.length === 0 ? (
        <div className="p-3 text-xs italic text-muted-foreground">No featured trends yet — check back after tomorrow&rsquo;s curation.</div>
      ) : (
        <ul className="divide-y divide-border max-h-80 overflow-y-auto">
          {trends.slice(0, 8).map((t) => {
            const angle = t.content_angle || {};
            return (
              <li key={t.id} className="p-2 text-xs" data-testid={`cockpit-trend-${t.id}`}>
                <div className="font-semibold truncate">{angle.headline || t.title}</div>
                {angle.hook && <div className="text-[10px] text-muted-foreground italic line-clamp-1">{angle.hook}</div>}
                <div className="flex items-center gap-1 mt-1">
                  {t.url && (
                    <a href={t.url} target="_blank" rel="noreferrer" className="text-[10px] underline text-muted-foreground">
                      <ExternalLink className="w-2.5 h-2.5 inline mr-0.5" /> Source
                    </a>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function SuggestedPlaybooksSection({ playbooks, brandId, onImported }) {
  const [importFor, setImportFor] = useState(null);
  const [ctx, setCtx] = useState(null);
  useEffect(() => {
    api.get(`/workspaces/${window.__ws || ""}`).catch(() => {});
  }, []);
  const openImport = async (p) => {
    setImportFor(p);
    try {
      // Fetch import context (packages + projects) for this brand.
      const wsId = (await api.get("/auth/me")).data?.memberships?.[0]?.workspace_id;
      const { data } = await api.get(`/workspaces/${wsId}/brands/${brandId}/import-context`);
      setCtx(data);
    } catch {
      setCtx({ packages: [], projects: [] });
    }
  };
  return (
    <section className="border border-border" data-testid="cockpit-playbooks">
      <SectionHead icon={Layers} title="Suggested playbooks" count={playbooks.length} />
      {playbooks.length === 0 ? (
        <div className="p-3 text-xs italic text-muted-foreground">Create playbooks under Marketing → Campaign playbooks.</div>
      ) : (
        <ul className="divide-y divide-border max-h-80 overflow-y-auto">
          {playbooks.slice(0, 6).map((p) => (
            <li key={p.id} className="p-2 flex items-center gap-2 text-xs" data-testid={`cockpit-playbook-${p.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{p.name}</div>
                <div className="text-[10px] text-muted-foreground truncate">
                  {(p.items || []).length} items · {p.category}
                </div>
              </div>
              <Button size="sm" className="rounded-none h-7 px-2 text-[11px]" onClick={() => openImport(p)} data-testid={`cockpit-playbook-import-${p.id}`}>
                <Send className="w-3 h-3 mr-1" /> Import
              </Button>
            </li>
          ))}
        </ul>
      )}
      {importFor && (
        <PlaybookImportPicker
          playbook={importFor} brandId={brandId} ctx={ctx}
          onClose={() => { setImportFor(null); setCtx(null); }}
          onDone={() => { setImportFor(null); setCtx(null); onImported?.(); }}
        />
      )}
    </section>
  );
}

function PlaybookImportPicker({ playbook, brandId, ctx, onClose, onDone }) {
  const packages = ctx?.packages || [];
  const projects = ctx?.projects || [];
  const [packageId, setPackageId] = useState(packages[0]?.id || "");
  const [projectId, setProjectId] = useState(projects[0]?.id || "");
  const [anchorDate, setAnchorDate] = useState(playbook.anchor?.day_date || new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        brand_id: brandId, anchor_date: anchorDate,
        package_id: packageId || null,
        project_id: !packageId && projectId ? projectId : null,
      };
      const { data } = await api.post(`/campaign-playbooks/${playbook.id}/import-to-brand`, payload);
      toast.success(`Imported ${data.content_item_ids.length} items + ${data.task_ids.length} task(s)`);
      onDone();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Import failed"));
    } finally { setBusy(false); }
  };
  const hasProject = !!(packageId && packages.find((p) => p.id === packageId)?.project_id) || !!projectId;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md rounded-none" data-testid="playbook-import-picker">
        <DialogHeader><DialogTitle>Import &ldquo;{playbook.name}&rdquo;</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {ctx === null && <div className="text-xs text-muted-foreground">Loading brand context…</div>}
          {ctx && (
            <>
              <div>
                <Label className="text-[10px] uppercase tracking-widest">Package (optional — resolves project for tasks)</Label>
                <Select value={packageId || "_none"} onValueChange={(v) => setPackageId(v === "_none" ? "" : v)}>
                  <SelectTrigger className="h-9 rounded-none" data-testid="playbook-import-package"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">No package</SelectItem>
                    {packages.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name} {p.project_id ? "· has project" : "· no project"}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {!packageId && (
                <div>
                  <Label className="text-[10px] uppercase tracking-widest">Or pick a project directly</Label>
                  <Select value={projectId || "_none"} onValueChange={(v) => setProjectId(v === "_none" ? "" : v)}>
                    <SelectTrigger className="h-9 rounded-none" data-testid="playbook-import-project"><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">No project</SelectItem>
                      {projects.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className="text-[10px] uppercase tracking-widest">Anchor date</Label>
                <Input type="date" value={anchorDate} onChange={(e) => setAnchorDate(e.target.value)} className="h-9 rounded-none" data-testid="playbook-import-picker-date" />
              </div>
              <div className={`text-[11px] p-2 border ${hasProject ? "border-emerald-500/40 bg-emerald-500/5 text-emerald-700" : "border-amber-500/40 bg-amber-500/5 text-amber-700"}`}>
                {hasProject
                  ? "✓ Tasks will spawn in the selected project, assigned to the content owner."
                  : "⚠ No project resolved — content items will be created but tasks won't spawn. Pick a package or project to get tasks."}
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy || !ctx} data-testid="playbook-import-picker-submit">
            <Send className="w-3 h-3 mr-1" /> {busy ? "Importing…" : "Import"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HeatmapSection({ heatmap, brandId, onChanged }) {
  const [selectedDate, setSelectedDate] = useState(null);
  const cells = useMemo(() => {
    const start = new Date(heatmap.window_start);
    const end = new Date(heatmap.window_end);
    const out = [];
    const cur = new Date(start);
    while (cur <= end) {
      const iso = cur.toISOString().slice(0, 10);
      out.push({ date: iso, count: heatmap.counts[iso] || 0 });
      cur.setDate(cur.getDate() + 1);
    }
    return out;
  }, [heatmap]);
  const maxCount = Math.max(1, ...cells.map((c) => c.count));
  const today = new Date().toISOString().slice(0, 10);
  return (
    <section className="border border-border" data-testid="cockpit-heatmap">
      <SectionHead icon={LayoutDashboard} title="Content calendar occupancy" sub="click a day to see scheduled posts · overload badge when >3 posts" />
      <div className="p-3">
        <div className="grid gap-0.5" style={{ gridTemplateColumns: "repeat(14, minmax(0, 1fr))" }}>
          {cells.map((c) => {
            const intensity = c.count === 0 ? 0 : Math.min(1, c.count / maxCount);
            const isToday = c.date === today;
            const overload = c.count > 3;
            const bg = c.count === 0 ? "hsl(var(--muted))" : overload ? `rgba(239,68,68,${0.4 + 0.5 * intensity})` : `rgba(16,185,129,${0.2 + 0.6 * intensity})`;
            return (
              <button
                key={c.date}
                type="button"
                onClick={() => c.count > 0 && setSelectedDate(c.date)}
                className={`aspect-square text-[8px] flex items-center justify-center font-mono ${c.count > 0 ? "hover:ring-1 hover:ring-primary cursor-pointer" : "cursor-default"}`}
                style={{ background: bg, outline: isToday ? "1.5px solid hsl(var(--primary))" : "none" }}
                title={`${c.date} — ${c.count} post${c.count === 1 ? "" : "s"}${overload ? " · OVERLOAD" : ""}`}
                data-testid={`heatmap-cell-${c.date}`}>
                {c.count > 0 && c.count}
              </button>
            );
          })}
        </div>
        <div className="mt-2 flex items-center justify-between text-[10px] text-muted-foreground">
          <span>{heatmap.window_start}</span>
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2" style={{ background: "rgba(16,185,129,0.5)" }} /> normal</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2" style={{ background: "rgba(239,68,68,0.6)" }} /> overload (&gt;3)</span>
          </div>
          <span>{heatmap.window_end}</span>
        </div>
      </div>
      {selectedDate && (
        <HeatmapDayDrawer
          brandId={brandId} date={selectedDate}
          onClose={() => setSelectedDate(null)}
          onChanged={onChanged}
        />
      )}
    </section>
  );
}

function HeatmapDayDrawer({ brandId, date, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/brands/${brandId}/content-items-by-day?date=${date}`);
      setData(d);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Couldn't load day"));
    }
  }, [brandId, date]);
  useEffect(() => { load(); }, [load]);

  const reschedule = async (item, offsetDays) => {
    setBusy(item.id);
    try {
      const d = new Date(date); d.setDate(d.getDate() + offsetDays);
      const iso = d.toISOString().slice(0, 10);
      await api.patch(`/content-items/${item.id}/reschedule`, { planned_date: iso });
      toast.success(`Moved to ${iso}`);
      onChanged?.();
      onClose();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Reschedule failed"));
    } finally { setBusy(null); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg rounded-none" data-testid="heatmap-day-drawer">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>{date}</span>
            {data?.overload && <span className="chip chip-rejected text-[10px]">Overloaded ({data.count} posts)</span>}
          </DialogTitle>
        </DialogHeader>
        {!data ? <div className="text-sm text-muted-foreground">Loading…</div> : data.items.length === 0 ? (
          <div className="text-xs italic text-muted-foreground">No posts scheduled.</div>
        ) : (
          <ul className="divide-y divide-border" data-testid="heatmap-day-list">
            {data.items.map((it) => (
              <li key={it.id} className="py-2 text-xs" data-testid={`heatmap-day-item-${it.id}`}>
                <div className="font-medium truncate">{it.name || it.topic}</div>
                <div className="text-[10px] text-muted-foreground truncate">
                  {it.channel || it.category} {it.playbook_name ? `· from playbook ${it.playbook_name}` : ""}
                  {it.status && ` · ${it.status}`}
                </div>
                <div className="flex items-center gap-1 mt-1">
                  <Button size="sm" variant="outline" className="rounded-none h-6 px-1.5 text-[10px]"
                          disabled={busy === it.id} onClick={() => reschedule(it, -1)} data-testid={`heatmap-day-back-${it.id}`}>
                    ← -1d
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-none h-6 px-1.5 text-[10px]"
                          disabled={busy === it.id} onClick={() => reschedule(it, 1)} data-testid={`heatmap-day-fwd-${it.id}`}>
                    +1d →
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-none h-6 px-1.5 text-[10px]"
                          disabled={busy === it.id} onClick={() => reschedule(it, 7)}>
                    +1w
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CampaignsSection({ campaigns, canEdit, onNew, onEdit, onDelete }) {
  const buckets = useMemo(() => {
    const b = { draft: [], live: [], paused: [], done: [] };
    (campaigns || []).forEach((c) => { (b[c.status] || b.draft).push(c); });
    return b;
  }, [campaigns]);
  return (
    <section className="border border-border" data-testid="cockpit-campaigns">
      <SectionHead
        icon={Rocket} title="Brand campaigns" count={(campaigns || []).length}
        action={canEdit && <Button size="sm" className="rounded-none h-7" onClick={onNew} data-testid="cockpit-campaign-new"><Plus className="w-3 h-3 mr-1" /> New campaign</Button>}
      />
      {(campaigns || []).length === 0 ? (
        <div className="p-3 text-xs italic text-muted-foreground">No campaigns yet — the first one is often a seasonal push tied to an upcoming marketable day.</div>
      ) : (
        <div className="grid gap-2 md:grid-cols-4 p-3">
          {["draft", "live", "paused", "done"].map((s) => (
            <div key={s} className="border border-border" data-testid={`cockpit-campaigns-${s}`}>
              <div className={`px-2 py-1.5 border-b border-border text-[10px] uppercase tracking-widest font-semibold ${CAMPAIGN_STATUS_TONE[s]}`}>{s} · {buckets[s].length}</div>
              <div className="p-2 space-y-1.5 max-h-60 overflow-y-auto">
                {buckets[s].length === 0 && <div className="text-[10px] italic text-muted-foreground">—</div>}
                {buckets[s].map((c) => (
                  <div key={c.id} className="border border-border p-1.5 text-[11px]" data-testid={`cockpit-campaign-${c.id}`}>
                    <div className="font-medium truncate">{c.name}</div>
                    {c.goal && <div className="text-[10px] text-muted-foreground truncate">{c.goal}</div>}
                    <div className="text-[9px] text-muted-foreground flex items-center gap-1 mt-0.5">
                      {c.start_date && <span>{c.start_date}</span>}
                      {c.budget && <span>· ₹{c.budget}</span>}
                    </div>
                    {canEdit && (
                      <div className="flex gap-1 mt-1">
                        <Button size="sm" variant="ghost" className="rounded-none h-5 px-1 text-[9px]" onClick={() => onEdit(c)} data-testid={`cockpit-campaign-edit-${c.id}`}><Edit3 className="w-2.5 h-2.5" /></Button>
                        <Button size="sm" variant="ghost" className="rounded-none h-5 px-1 text-[9px] text-destructive" onClick={() => onDelete(c)} data-testid={`cockpit-campaign-delete-${c.id}`}><Trash2 className="w-2.5 h-2.5" /></Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function OutreachSection({ items, canEdit, onNew, onEdit, onDelete }) {
  const buckets = useMemo(() => {
    const b = { to_reach: [], contacted: [], responded: [], declined: [], won: [] };
    (items || []).forEach((o) => { (b[o.status] || b.to_reach).push(o); });
    return b;
  }, [items]);
  return (
    <section className="border border-border" data-testid="cockpit-outreach">
      <SectionHead
        icon={PhoneCall} title="Outreach & partnerships"
        count={(items || []).length}
        sub="collabs, influencers, PR contacts for this brand"
        action={canEdit && <Button size="sm" className="rounded-none h-7" onClick={onNew} data-testid="cockpit-outreach-new"><Plus className="w-3 h-3 mr-1" /> New target</Button>}
      />
      {(items || []).length === 0 ? (
        <div className="p-3 text-xs italic text-muted-foreground">Track influencers, brand partners and PR contacts as an actionable pipeline.</div>
      ) : (
        <div className="grid gap-2 md:grid-cols-5 p-3">
          {["to_reach", "contacted", "responded", "declined", "won"].map((s) => (
            <div key={s} className="border border-border" data-testid={`cockpit-outreach-${s}`}>
              <div className={`px-2 py-1.5 border-b border-border text-[10px] uppercase tracking-widest font-semibold ${OUTREACH_STATUS_TONE[s]}`}>{s.replace(/_/g, " ")} · {buckets[s].length}</div>
              <div className="p-2 space-y-1.5 max-h-60 overflow-y-auto">
                {buckets[s].length === 0 && <div className="text-[10px] italic text-muted-foreground">—</div>}
                {buckets[s].map((o) => (
                  <div key={o.id} className="border border-border p-1.5 text-[11px]" data-testid={`cockpit-outreach-item-${o.id}`}>
                    <div className="font-medium truncate">{o.name}</div>
                    {o.org && <div className="text-[10px] text-muted-foreground truncate">{o.org}</div>}
                    <div className="text-[9px] text-muted-foreground truncate">{o.channel}{o.next_step ? ` · ${o.next_step}` : ""}</div>
                    {canEdit && (
                      <div className="flex gap-1 mt-1">
                        <Button size="sm" variant="ghost" className="rounded-none h-5 px-1 text-[9px]" onClick={() => onEdit(o)} data-testid={`cockpit-outreach-edit-${o.id}`}><Edit3 className="w-2.5 h-2.5" /></Button>
                        <Button size="sm" variant="ghost" className="rounded-none h-5 px-1 text-[9px] text-destructive" onClick={() => onDelete(o)} data-testid={`cockpit-outreach-delete-${o.id}`}><Trash2 className="w-2.5 h-2.5" /></Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function SectionHead({ icon: Icon, title, count, sub, action }) {
  return (
    <div className="px-3 py-2 bg-muted/40 border-b border-border flex items-center gap-2 text-xs">
      <Icon className="w-3.5 h-3.5 text-primary" />
      <span className="font-semibold uppercase tracking-widest">{title}</span>
      {typeof count === "number" && <span className="normal-case text-[10px] text-muted-foreground">· {count}</span>}
      {sub && <span className="normal-case text-[10px] text-muted-foreground italic">— {sub}</span>}
      {action && <div className="ml-auto">{action}</div>}
    </div>
  );
}

// ─────────── Editors ───────────

function CampaignEditor({ wsId, initial, onClose, onSaved }) {
  const isNew = !initial?.id;
  const [form, setForm] = useState({
    name: initial?.name || "",
    description: initial?.description || "",
    goal: initial?.goal || "",
    status: initial?.status || "draft",
    start_date: initial?.start_date || "",
    end_date: initial?.end_date || "",
    budget: initial?.budget || "",
    channels: (initial?.channels || []).join(", "),
    tags: (initial?.tags || []).join(", "),
    notes: initial?.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    const payload = {
      ...(isNew ? { scope: initial.scope, brand_id: initial.brand_id } : {}),
      name: form.name, description: form.description, goal: form.goal,
      status: form.status,
      start_date: form.start_date || null, end_date: form.end_date || null,
      budget: form.budget ? Number(form.budget) : null,
      channels: form.channels.split(",").map((c) => c.trim()).filter(Boolean),
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      notes: form.notes,
    };
    setBusy(true);
    try {
      if (isNew) await api.post(`/workspaces/${wsId}/campaigns`, payload);
      else await api.patch(`/campaigns/${initial.id}`, payload);
      toast.success(isNew ? "Campaign created" : "Campaign updated");
      onSaved();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Save failed"));
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl rounded-none" data-testid="cockpit-campaign-editor">
        <DialogHeader><DialogTitle>{isNew ? "New campaign" : "Edit campaign"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          <div className="col-span-2">
            <Label className="text-[10px] uppercase tracking-widest">Name</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="cockpit-campaign-name" />
          </div>
          <div className="col-span-2">
            <Label className="text-[10px] uppercase tracking-widest">Goal</Label>
            <Input value={form.goal} onChange={(e) => set("goal", e.target.value)} placeholder="10K new IG followers" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Status</Label>
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger className="h-9 rounded-none"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["draft", "live", "paused", "done"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Budget (₹)</Label>
            <Input type="number" value={form.budget} onChange={(e) => set("budget", e.target.value)} />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Start</Label>
            <Input type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">End</Label>
            <Input type="date" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} />
          </div>
          <div className="col-span-2">
            <Label className="text-[10px] uppercase tracking-widest">Channels (comma)</Label>
            <Input value={form.channels} onChange={(e) => set("channels", e.target.value)} placeholder="instagram, facebook, ads" />
          </div>
          <div className="col-span-2">
            <Label className="text-[10px] uppercase tracking-widest">Tags (comma)</Label>
            <Input value={form.tags} onChange={(e) => set("tags", e.target.value)} />
          </div>
          <div className="col-span-2">
            <Label className="text-[10px] uppercase tracking-widest">Description / notes</Label>
            <Textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy} data-testid="cockpit-campaign-save">
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OutreachEditor({ wsId, initial, onClose, onSaved }) {
  const isNew = !initial?.id;
  const [form, setForm] = useState({
    name: initial?.name || "", org: initial?.org || "",
    channel: initial?.channel || "email", contact: initial?.contact || "",
    url: initial?.url || "", status: initial?.status || "to_reach",
    next_step: initial?.next_step || "", next_step_date: initial?.next_step_date || "",
    notes: initial?.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    const payload = {
      ...(isNew ? { scope: initial.scope, brand_id: initial.brand_id } : {}),
      ...form,
    };
    setBusy(true);
    try {
      if (isNew) await api.post(`/workspaces/${wsId}/outreach-targets`, payload);
      else await api.patch(`/outreach-targets/${initial.id}`, payload);
      toast.success(isNew ? "Target added" : "Target updated");
      onSaved();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Save failed"));
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-xl rounded-none" data-testid="cockpit-outreach-editor">
        <DialogHeader><DialogTitle>{isNew ? "New outreach target" : "Edit target"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Name</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="cockpit-outreach-name" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Org</Label>
            <Input value={form.org} onChange={(e) => set("org", e.target.value)} />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Channel</Label>
            <Select value={form.channel} onValueChange={(v) => set("channel", v)}>
              <SelectTrigger className="h-9 rounded-none"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["email", "linkedin", "twitter", "instagram", "phone", "other"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Contact (email/handle)</Label>
            <Input value={form.contact} onChange={(e) => set("contact", e.target.value)} />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Status</Label>
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger className="h-9 rounded-none"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["to_reach", "contacted", "responded", "declined", "won"].map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Next step date</Label>
            <Input type="date" value={form.next_step_date} onChange={(e) => set("next_step_date", e.target.value)} />
          </div>
          <div className="col-span-2">
            <Label className="text-[10px] uppercase tracking-widest">Next step</Label>
            <Input value={form.next_step} onChange={(e) => set("next_step", e.target.value)} placeholder="Send intro DM" />
          </div>
          <div className="col-span-2">
            <Label className="text-[10px] uppercase tracking-widest">Notes</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy} data-testid="cockpit-outreach-save">
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChannelsEditor({ brand, onClose, onSaved }) {
  const [rows, setRows] = useState(brand.marketing_channels?.length ? brand.marketing_channels : [{ platform: "instagram", handle: "", url: "", followers_manual: 0 }]);
  const [busy, setBusy] = useState(false);
  const upd = (i, k, v) => setRows((prev) => prev.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const add = () => setRows((p) => [...p, { platform: "instagram", handle: "", url: "", followers_manual: 0 }]);
  const rm = (i) => setRows((p) => p.filter((_, idx) => idx !== i));
  const save = async () => {
    setBusy(true);
    try {
      const payload = rows.map((r) => ({
        platform: r.platform, handle: r.handle || null, url: r.url || null,
        followers_manual: r.followers_manual != null && r.followers_manual !== "" ? Number(r.followers_manual) : null,
        last_post_at: r.last_post_at || null,
      }));
      await api.patch(`/brands/${brand.id}/channels`, { channels: payload });
      toast.success("Channels saved");
      onSaved();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Save failed"));
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl rounded-none" data-testid="cockpit-channels-editor">
        <DialogHeader><DialogTitle>Marketing channels</DialogTitle></DialogHeader>
        <div className="space-y-2 max-h-[60vh] overflow-y-auto">
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-end border border-border p-2" data-testid={`cockpit-channel-row-${i}`}>
              <div className="col-span-3">
                <Label className="text-[9px] uppercase tracking-widest">Platform</Label>
                <Select value={r.platform} onValueChange={(v) => upd(i, "platform", v)}>
                  <SelectTrigger className="h-8 rounded-none text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.keys(PLATFORM_ICONS).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-3">
                <Label className="text-[9px] uppercase tracking-widest">Handle</Label>
                <Input value={r.handle || ""} onChange={(e) => upd(i, "handle", e.target.value)} className="h-8 rounded-none text-xs" placeholder="@brand" />
              </div>
              <div className="col-span-3">
                <Label className="text-[9px] uppercase tracking-widest">URL</Label>
                <Input value={r.url || ""} onChange={(e) => upd(i, "url", e.target.value)} className="h-8 rounded-none text-xs" />
              </div>
              <div className="col-span-2">
                <Label className="text-[9px] uppercase tracking-widest">Followers</Label>
                <Input type="number" value={r.followers_manual ?? ""} onChange={(e) => upd(i, "followers_manual", e.target.value)} className="h-8 rounded-none text-xs" />
              </div>
              <div className="col-span-1 flex items-end">
                <Button size="sm" variant="ghost" className="rounded-none h-8 w-full text-destructive" onClick={() => rm(i)}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}
          <Button size="sm" variant="outline" className="rounded-none" onClick={add} data-testid="cockpit-channels-add">
            <Plus className="w-3 h-3 mr-1" /> Add channel
          </Button>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={save} disabled={busy} data-testid="cockpit-channels-save">
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
